import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiRequestError,
  deleteCustomFieldMapping,
  getCustomFieldMappings,
  setCustomFieldEditable,
} from "../lib/client";
import type { SalesforceCustomFieldMapping } from "../lib/types";

/**
 * List + soft-delete a subscriber's Salesforce ↔ Instil custom-field mappings,
 * and toggle whether each Instil custom field is editable.
 * Loads on mount / subscriber change. Soft-delete is irreversible from the UI
 * (upstream clears `deleted_at` only if the mapping is recreated later).
 * Editability is one flag per Instil custom field, so rows that share an
 * Instil id update together.
 */
export function CustomFieldMappings({
  subscriberId,
}: {
  subscriberId: string;
}) {
  const [mappings, setMappings] = useState<SalesforceCustomFieldMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [objectFilter, setObjectFilter] = useState("");
  // Bumped on each load / subscriber change / unmount so only the latest
  // in-flight request is allowed to write state.
  const loadRequestIdRef = useRef(0);
  // Generation of the in-flight editability toggle, plus the subscriber it
  // belongs to. Subscriber changes bump the generation so a PATCH started for
  // the previous subscriber cannot write mappings or the notice here.
  const editRequestRef = useRef({ id: 0, subscriberId: "" });

  function beginLoad(nextSubscriberId: string, resetFilters = false) {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    setError(null);
    setNotice(null);
    if (resetFilters) {
      setFilter("");
      setObjectFilter("");
    }
    getCustomFieldMappings(nextSubscriberId)
      .then((rows) => {
        if (requestId !== loadRequestIdRef.current) return;
        setMappings(rows);
      })
      .catch((err) => {
        if (requestId !== loadRequestIdRef.current) return;
        setMappings([]);
        setError(
          err instanceof ApiRequestError
            ? err.message
            : "Failed to load custom field mappings.",
        );
      })
      .finally(() => {
        if (requestId === loadRequestIdRef.current) setLoading(false);
      });
  }

  function load() {
    beginLoad(subscriberId);
  }

  useEffect(() => {
    editRequestRef.current = {
      id: editRequestRef.current.id + 1,
      subscriberId,
    };
    setUpdatingId(null);
    beginLoad(subscriberId, true);
    return () => {
      loadRequestIdRef.current += 1;
      editRequestRef.current = {
        id: editRequestRef.current.id + 1,
        subscriberId: editRequestRef.current.subscriberId,
      };
    };
  }, [subscriberId]);

  const objects = useMemo(() => {
    const set = new Set(mappings.map((m) => m.salesforce_object_name));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [mappings]);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return mappings
      .filter((m) => {
        if (objectFilter && m.salesforce_object_name !== objectFilter)
          return false;
        if (!q) return true;
        return (
          m.salesforce_custom_field_label.toLowerCase().includes(q) ||
          m.salesforce_custom_field_name.toLowerCase().includes(q) ||
          m.salesforce_object_name.toLowerCase().includes(q) ||
          m.instil_custom_field_id.toLowerCase().includes(q) ||
          m.instil_custom_field_type.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => {
        const byObject = a.salesforce_object_name.localeCompare(
          b.salesforce_object_name,
        );
        if (byObject !== 0) return byObject;
        return a.salesforce_custom_field_name.localeCompare(
          b.salesforce_custom_field_name,
        );
      });
  }, [mappings, filter, objectFilter]);

  async function remove(mapping: SalesforceCustomFieldMapping) {
    const label = `${mapping.salesforce_object_name}.${mapping.salesforce_custom_field_name}`;
    const ok = window.confirm(
      `Soft-delete the mapping for ${label}?\n\n` +
        `Instil custom field: ${mapping.instil_custom_field_id}\n\n` +
        "It will stop applying on the next import. Recreate the mapping later to restore it.",
    );
    if (!ok) return;

    setDeletingId(mapping.instil_custom_field_id);
    setError(null);
    setNotice(null);
    try {
      await deleteCustomFieldMapping(subscriberId, {
        instilCustomFieldId: mapping.instil_custom_field_id,
      });
      setMappings((prev) =>
        prev.filter(
          (m) => m.instil_custom_field_id !== mapping.instil_custom_field_id,
        ),
      );
      setNotice(`Removed ${label}.`);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Failed to delete custom field mapping.",
      );
    } finally {
      setDeletingId(null);
    }
  }

  async function setEditable(
    mapping: SalesforceCustomFieldMapping,
    isEditable: boolean,
  ) {
    if (mapping.is_editable_in_instil === isEditable) return;
    const request = {
      id: editRequestRef.current.id + 1,
      subscriberId,
    };
    editRequestRef.current = request;
    const id = mapping.instil_custom_field_id;
    setUpdatingId(id);
    setError(null);
    setNotice(null);
    const stillCurrent = () =>
      request.id === editRequestRef.current.id &&
      request.subscriberId === editRequestRef.current.subscriberId;
    try {
      const result = await setCustomFieldEditable(subscriberId, id, isEditable);
      if (!stillCurrent()) return;
      setMappings((prev) =>
        prev.map((row) =>
          row.instil_custom_field_id === id
            ? { ...row, is_editable_in_instil: result.is_editable }
            : row,
        ),
      );
      const label = mapping.salesforce_custom_field_label;
      setNotice(
        result.is_editable
          ? `${label} is editable in Instil.`
          : `${label} is read-only in Instil.`,
      );
    } catch (err) {
      if (!stillCurrent()) return;
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Failed to update custom field editability.",
      );
    } finally {
      if (stillCurrent()) setUpdatingId(null);
    }
  }

  if (loading && mappings.length === 0 && !error) {
    return <div className="placeholder">Loading custom field mappings…</div>;
  }

  return (
    <section className="panel">
      <div className="toolbar">
        <button
          type="button"
          className="btn"
          onClick={load}
          disabled={loading || deletingId !== null || updatingId !== null}
        >
          {loading ? "Loading…" : "Reload"}
        </button>
        <span className="count">
          {mappings.length} active mapping{mappings.length === 1 ? "" : "s"}
        </span>
      </div>

      <p className="hint footnote">
        Soft-delete removes a mapping from the active set (sets{" "}
        <code>deleted_at</code>). It stops applying on the next import; recreate
        the mapping later to restore it. The Editable checkbox updates the
        Instil custom field definition. A later Salesforce custom-field import
        writes editability from the Salesforce field type.
      </p>

      {error && <div className="banner error">{error}</div>}
      {notice && <div className="banner ok">{notice}</div>}

      {mappings.length > 0 && (
        <div className="toolbar">
          <input
            className="text-input grow"
            placeholder={`Filter ${mappings.length} mappings by name, object, or Instil id…`}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <select
            className="text-input"
            value={objectFilter}
            onChange={(e) => setObjectFilter(e.target.value)}
            aria-label="Filter by Salesforce object"
          >
            <option value="">All objects</option>
            {objects.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <span className="count">{rows.length} shown</span>
        </div>
      )}

      {!loading && mappings.length === 0 && !error ? (
        <div className="placeholder">
          No active custom field mappings for this subscriber.
        </div>
      ) : rows.length === 0 && mappings.length > 0 ? (
        <div className="placeholder">No mappings match the current filter.</div>
      ) : mappings.length > 0 ? (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>SF object</th>
                <th>SF label</th>
                <th>SF API name</th>
                <th>SF type</th>
                <th>Instil CF id</th>
                <th>Instil type</th>
                <th>Editable</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const busy = deletingId === m.instil_custom_field_id;
                const saving = updatingId === m.instil_custom_field_id;
                return (
                  <tr
                    key={`${m.salesforce_object_name}:${m.instil_custom_field_id}`}
                  >
                    <td>
                      <span className="tag">{m.salesforce_object_name}</span>
                    </td>
                    <td>{m.salesforce_custom_field_label}</td>
                    <td>
                      <button
                        type="button"
                        className="copyable"
                        title="Click to copy"
                        onClick={() =>
                          navigator.clipboard?.writeText(
                            m.salesforce_custom_field_name,
                          )
                        }
                      >
                        {m.salesforce_custom_field_name}
                      </button>
                    </td>
                    <td>
                      <span className="tag">
                        {m.salesforce_custom_field_type}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="copyable"
                        title="Click to copy"
                        onClick={() =>
                          navigator.clipboard?.writeText(
                            m.instil_custom_field_id,
                          )
                        }
                      >
                        {m.instil_custom_field_id}
                      </button>
                    </td>
                    <td>
                      <span className="tag">{m.instil_custom_field_type}</span>
                    </td>
                    <td>
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={m.is_editable_in_instil}
                          disabled={deletingId !== null || updatingId !== null}
                          aria-label={`Editable in Instil: ${m.salesforce_custom_field_label}`}
                          onChange={(e) => setEditable(m, e.target.checked)}
                        />
                        {saving
                          ? "Saving…"
                          : m.is_editable_in_instil
                            ? "yes"
                            : "no"}
                      </label>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn danger"
                        disabled={deletingId !== null || updatingId !== null}
                        onClick={() => remove(m)}
                      >
                        {busy ? "Removing…" : "Remove"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
